import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchInvoiceById } from "../api/vehicles";
import PayWithWaveButton from "../components/PayWithWaveButton";
import { generateDocumentPDF } from '../utils/generateDocumentPDF';

const formatFCFA = (num) =>
  num ? `${Number(num).toLocaleString("fr-CI")} FCFA` : "0 FCFA";

const OwnerInvoiceByIdPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !token) return;
    fetchInvoiceById(id, token)
      .then(setInvoice)
      .catch(err => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [id, token]);

  if (loading) return <div className="text-slate-400 p-8">Loading invoice…</div>;
  if (error) return <div className="text-red-400 p-8">{error}</div>;
  if (!invoice) return <div className="text-slate-400 p-8">Invoice not found.</div>;

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-white">
          Invoice: <span className="text-blue-400 text-lg font-mono">{invoice._id}</span>
        </h2>
        <button
          onClick={() => generateDocumentPDF(invoice, 'invoice', {
            businessName: invoice.workshop?.businessName,
            address: invoice.workshop?.address,
            phone: invoice.workshop?.phone,
            logo: invoice.workshop?.logo,
          })}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white transition-colors flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Télécharger PDF
        </button>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 mb-4 space-y-1">
        <p className="text-white"><span className="text-slate-400">Workshop:</span> {invoice.workshop?.businessName || "-"}</p>
        <p className="text-white"><span className="text-slate-400">Client:</span> {invoice.client?.name || "-"}</p>
        <p className="text-white"><span className="text-slate-400">Vehicle:</span> {invoice.vehicle?.plateNumber || "-"}</p>
        <div className="pt-2">
          {invoice.paid ? (
            <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-lg text-sm font-bold">
              PAYÉ / PAID ✓
            </span>
          ) : (
            <span className="bg-yellow-500/15 text-yellow-400 border border-yellow-500/20 px-3 py-1 rounded-lg text-sm font-bold">
              IMPAYÉ / UNPAID
            </span>
          )}
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden mb-4">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Reference</th>
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Designation</th>
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Brand</th>
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Qty</th>
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Unit Price</th>
              <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {invoice.items?.map((item, idx) => (
              <tr key={idx} className="hover:bg-slate-700/20 transition-colors">
                <td className="p-3 text-white">{item.reference || "-"}</td>
                <td className="p-3 text-white">{item.designation || item.description || "-"}</td>
                <td className="p-3 text-white">{item.brand || "-"}</td>
                <td className="p-3 text-white">{item.quantity ?? 0}</td>
                <td className="p-3 text-white">{formatFCFA(item.clientPrice ?? item.unitPrice ?? item.price ?? 0)}</td>
                <td className="p-3 text-white font-medium">
                  {formatFCFA((item.clientPrice ?? item.unitPrice ?? item.price ?? 0) * (item.quantity ?? 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 mb-4">
        {invoice.tax > 0 ? (
          <div className="space-y-1 text-right">
            <div className="text-slate-300">Sous-total HT : <span className="text-white font-medium">{formatFCFA(invoice.clientSubtotal || invoice.subtotal)}</span></div>
            <div className="text-slate-300">TVA (18%) : <span className="text-white font-medium">{formatFCFA(invoice.tax)}</span></div>
            <div className="text-emerald-400 font-bold text-lg">Total TTC : {formatFCFA(invoice.clientTotal || invoice.total)}</div>
          </div>
        ) : (
          <div className="text-right text-emerald-400 font-bold text-lg">Total : {formatFCFA(invoice.clientTotal || invoice.total)}</div>
        )}
        {invoice.depositAmount > 0 && (
          <div className="text-right text-yellow-400 text-sm mt-1">
            Acompte : {formatFCFA(invoice.depositAmount)}
          </div>
        )}
      </div>

      {!invoice.paid && (() => {
        // invoice.total already includes tax if applicable — no need to recalculate
        const invoiceTotal = invoice.clientTotal || invoice.total;
        const alreadyPaid = Number(invoice.paidAmount || 0);
        const deposit = Number(invoice.depositAmount || 0);
        const depositNotYetPaid = deposit > 0 && deposit < invoiceTotal && alreadyPaid < deposit;
        const amountToPay = Math.round(
          depositNotYetPaid
            ? deposit - alreadyPaid
            : invoiceTotal - alreadyPaid
        );

        return (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
            <h3 className="text-base font-semibold text-white mb-3">Paiement Mobile Money</h3>
            <div className="mb-3 space-y-1 text-sm">
              {alreadyPaid > 0 && (
                <div className="text-emerald-400 font-semibold">
                  Déjà payé : {formatFCFA(alreadyPaid)}
                </div>
              )}
              {depositNotYetPaid ? (
                <div className="text-yellow-400 font-semibold">
                  Acompte requis : {formatFCFA(deposit)}
                  {alreadyPaid > 0 && ` (reste : ${formatFCFA(amountToPay)})`}
                </div>
              ) : (
                <div className="text-yellow-400 font-semibold">
                  Solde restant : {formatFCFA(amountToPay)}
                </div>
              )}
            </div>
            <PayWithWaveButton
              vehicleId={invoice.vehicle?._id || invoice.vehicle}
              amount={amountToPay}
              type="invoice"
              invoiceId={invoice._id}
              workshopId={invoice.workshop?._id || invoice.workshop}
              onSuccess={() => {
                alert("Paiement réussi !");
                window.location.reload();
              }}
            />
          </div>
        );
      })()}
    </div>
  );
};

export default OwnerInvoiceByIdPage;
